import { useCallback, useEffect, useState } from 'react'
import {
  INTEGRATION_HEALTH_LABELS,
  WEBHOOK_STATUS_LABELS,
  integrationsApi,
  type IntegrationCallsPage,
  type IntegrationCenter,
  type IntegrationCenterItem,
  type IntegrationHealth,
  type LeadIntakeClient,
  type Webhook,
  type WebhookDelivery,
  type WebhookDeliveryStatus,
  type WebhookEventType,
} from '../api/integrations'
import { LeadAdsTab } from './LeadAdsTab'
import { SyncPauseCard } from '../components/SyncPauseCard'
import { SkyCanvassCard } from '../components/SkyCanvassCard'
import { leadProvidersApi, type LeadProvider } from '../api/leadProviders'
import { campaignsApi, type Campaign } from '../api/campaigns'
import { leadsApi, type LeadSourceOption } from '../api/leads'
import {
  FormGrid,
  Page,
  PageHeader,
  Tabs,
  FilterBar,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  CardHeader,
  CardContent,
  DataTable,
  StatusPill,
  TextField,
  Select,
  Checkbox,
  type PillTone,
  useConfirm,
  OverflowMenu,
  usePrompt,
} from '../components/ui'

/**
 * F-075 (TASK-0329): integrazioni. Tre schede, tutte riservate all'Admin (il server risponde 403 agli altri):
 * le chiavi con cui un portale lead o una landing invia contatti a `POST /api/v1/leads` (fonte e campagna
 * le decide la chiave, non il messaggio), i webhook in uscita firmati (HMAC-SHA256, intestazione
 * `X-Equal-Signature`, tolleranza di 5 minuti) e il registro delle consegne con ripetizione manuale.
 * La chiave e il segreto si vedono UNA volta, alla creazione o alla rotazione: poi non si possono rileggere.
 */
type Tab = 'overview' | 'keys' | 'ads' | 'webhooks' | 'deliveries'

const STATUS_TONES: Record<WebhookDeliveryStatus, PillTone> = {
  InCoda: 'neutral',
  Consegnata: 'success',
  Fallita: 'error',
  Ritento: 'warning',
}

function fmt(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('it-IT') : '—'
}

function errText(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

/** Il valore che si vede una volta sola, con il pulsante per copiarlo. */
function SecretBox({ title, value, onClose }: { title: string; value: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <Callout tone="warning">
      <div className="mb-1 md-typescale-emphasized-body-medium">{title}</div>
      <div className="md-typescale-body-small mb-2">Copialo adesso: non sarà più possibile rileggerlo. Se lo perdi, ruotalo.</div>
      <div className="d-flex gap-2 align-items-start flex-wrap">
        <div className="flex-grow-1">
          <TextField label={title} labelHidden className="font-monospace" readOnly value={value} onFocus={e => e.target.select()} />
        </div>
        <Button variant="outlined"
          onClick={() => { void navigator.clipboard?.writeText(value).then(() => setCopied(true)) }}>
          {copied ? 'Copiato' : 'Copia'}
        </Button>
        <Button variant="text" onClick={onClose}>Chiudi</Button>
      </div>
    </Callout>
  )
}

function KeysTab() {
  const askConfirm = useConfirm()
  const [clients, setClients] = useState<LeadIntakeClient[]>([])
  const [sources, setSources] = useState<LeadSourceOption[]>([])
  const [providers, setProviders] = useState<LeadProvider[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [error, setError] = useState<string | null>(null)
  const [shown, setShown] = useState<{ title: string; value: string } | null>(null)
  const [form, setForm] = useState({ name: '', source: '', providerId: '', campaignId: '', limit: '60' })
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      const [c, s, p, camp] = await Promise.all([
        integrationsApi.listClients(), leadsApi.sources(), leadProvidersApi.list(), campaignsApi.list(),
      ])
      setClients(c)
      setSources(s)
      setProviders(p)
      setCampaigns(camp.items)
      setError(null)
    } catch (e) {
      setError(errText(e, 'Errore nel caricamento'))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function create() {
    if (!form.name.trim()) return setError('Il nome è obbligatorio')
    if (!form.source) return setError('Scegli la fonte: è quella che avranno tutti i lead di questa chiave')
    const limit = Number(form.limit)
    if (!Number.isInteger(limit) || limit < 1 || limit > 600) return setError('Il limite è un intero da 1 a 600 richieste al minuto')
    setSaving(true)
    setError(null)
    try {
      const created = await integrationsApi.createClient({
        name: form.name.trim(),
        source: form.source,
        leadProviderId: form.providerId ? Number(form.providerId) : null,
        campaignId: form.campaignId ? Number(form.campaignId) : null,
        perMinuteLimit: limit,
      })
      setShown({ title: `Chiave "${created.name}"`, value: created.key ?? '' })
      setForm({ name: '', source: '', providerId: '', campaignId: '', limit: '60' })
      await load()
    } catch (e) {
      setError(errText(e, 'Errore nella creazione'))
    } finally {
      setSaving(false)
    }
  }

  async function rotate(c: LeadIntakeClient) {
    if (!(await askConfirm({ title: 'Ruota chiave', message: `Ruotare la chiave "${c.name}"? La vecchia smette di funzionare subito.`, confirmLabel: 'Ruota', destructive: true }))) return
    try {
      const r = await integrationsApi.rotateClient(c.id)
      setShown({ title: `Nuova chiave "${r.name}"`, value: r.key ?? '' })
      await load()
    } catch (e) {
      setError(errText(e, 'Errore nella rotazione'))
    }
  }

  async function revoke(c: LeadIntakeClient) {
    if (!(await askConfirm({ title: 'Revoca chiave', message: `Revocare la chiave "${c.name}"? Smette di funzionare subito e non si può riattivare.`, confirmLabel: 'Revoca', destructive: true }))) return
    try {
      await integrationsApi.revokeClient(c.id)
      await load()
    } catch (e) {
      setError(errText(e, 'Errore nella revoca'))
    }
  }

  return (
    <>
      {error && <Callout tone="error">{error}</Callout>}
      {shown && <SecretBox title={shown.title} value={shown.value} onClose={() => setShown(null)} />}

      <Card variant="outlined" padding="none">
        <CardHeader title="Nuova chiave di acquisizione" />
        <CardContent>
          <div className="d-flex flex-column gap-3">
            <p className="text-muted md-typescale-body-small mb-0">
              Il portale invia <code>POST /api/v1/leads</code> con l&apos;intestazione <code>X-Api-Key</code>. Usa la
              chiave da un server, non da una pagina nel browser. Lo stesso <code>externalId</code> inviato due
              volte crea un solo lead. Fonte e campagna sono quelle della chiave, qualunque cosa dica il messaggio.
            </p>
            <FormGrid columns={4}>
              <div>
                <TextField id="ik-name" label="Nome" maxLength={100} value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <Select id="ik-source" label="Fonte" value={form.source}
                  onChange={e => setForm(f => ({ ...f, source: e.target.value }))}>
                  <option value="">Scegli…</option>
                  {sources.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                </Select>
              </div>
              <div>
                <Select id="ik-provider" label="Fornitore (facoltativo)" value={form.providerId}
                  onChange={e => setForm(f => ({ ...f, providerId: e.target.value }))}>
                  <option value="">Nessuno</option>
                  {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Select>
              </div>
              <div>
                <Select id="ik-campaign" label="Campagna (facoltativa)" value={form.campaignId}
                  onChange={e => setForm(f => ({ ...f, campaignId: e.target.value }))}>
                  <option value="">Nessuna</option>
                  {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </div>
              <div>
                <TextField id="ik-limit" label="Al minuto" inputMode="numeric" value={form.limit}
                  onChange={e => setForm(f => ({ ...f, limit: e.target.value }))} />
              </div>
              <div>
                <Button variant="tonal" fullWidth disabled={saving} onClick={() => void create()}>
                  Crea
                </Button>
              </div>
            </FormGrid>
          </div>
        </CardContent>
      </Card>

      <Card variant="outlined" padding="none">
        {clients.length === 0 ? (
          <EmptyState icon="key" title="Nessuna chiave." />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Nome</th><th>Chiave</th><th>Fonte</th><th>Fornitore</th><th>Campagna</th>
                <th>Limite/min</th><th>Oggi</th><th>Ultimo uso</th><th>Stato</th><th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {clients.map(c => (
                <tr key={c.id}>
                  <td className="md-typescale-emphasized-body-medium">{c.name}</td>
                  <td><code>{c.keyPrefix}…</code></td>
                  <td>{sources.find(s => s.code === c.source)?.label ?? c.source}</td>
                  <td>{providers.find(p => p.id === c.leadProviderId)?.name ?? '—'}</td>
                  <td>{campaigns.find(x => x.id === c.campaignId)?.name ?? '—'}</td>
                  <td className="md-num">{c.perMinuteLimit}</td>
                  <td title="Lead ricevuti oggi (ora di Roma) / quota giornaliera, 0 = illimitata">
                    {c.usedToday ?? 0}{c.dailyQuota ? ` / ${c.dailyQuota}` : ' (nessuna quota)'}
                    {c.dailyQuota && (c.usedToday ?? 0) >= c.dailyQuota
                      ? <StatusPill tone="warning" className="ms-1">Quota raggiunta</StatusPill>
                      : null}
                  </td>
                  <td>{fmt(c.lastUsedAt)}</td>
                  <td>
                    {c.revokedAt
                      ? <StatusPill tone="error">Revocata</StatusPill>
                      : <StatusPill tone="success">Attiva</StatusPill>}
                  </td>
                  <td>
                    {!c.revokedAt && (
                      <div className="d-flex gap-1">
                        <IconButton icon="autorenew" size="sm" aria-label="Ruota la chiave"
                          onClick={() => void rotate(c)} />
                        <IconButton icon="block" size="sm" aria-label="Revoca"
                          onClick={() => void revoke(c)} />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>
    </>
  )
}

function WebhooksTab() {
  const askConfirm = useConfirm()
  const [hooks, setHooks] = useState<Webhook[]>([])
  const [types, setTypes] = useState<WebhookEventType[]>([])
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [shown, setShown] = useState<{ title: string; value: string } | null>(null)
  const [form, setForm] = useState<{ id?: number; name: string; url: string; codes: string[]; active: boolean }>(
    { name: '', url: '', codes: [], active: true })
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      const [h, t] = await Promise.all([integrationsApi.listWebhooks(), integrationsApi.eventTypes()])
      setHooks(h)
      setTypes(t)
      setError(null)
    } catch (e) {
      setError(errText(e, 'Errore nel caricamento'))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  function reset() {
    setForm({ name: '', url: '', codes: [], active: true })
  }

  function toggleCode(code: string) {
    setForm(f => ({ ...f, codes: f.codes.includes(code) ? f.codes.filter(c => c !== code) : [...f.codes, code] }))
  }

  async function save() {
    if (!form.name.trim()) return setError('Il nome è obbligatorio')
    if (!form.url.trim().toLowerCase().startsWith('https://')) return setError('L\'indirizzo deve iniziare con https://')
    if (form.codes.length === 0) return setError('Scegli almeno un evento')
    setSaving(true)
    setError(null)
    setInfo(null)
    try {
      const body = { name: form.name.trim(), url: form.url.trim(), eventCodes: form.codes }
      if (form.id === undefined) {
        const created = await integrationsApi.createWebhook(body)
        setShown({ title: `Segreto di firma di "${created.name}"`, value: created.secret ?? '' })
      } else {
        await integrationsApi.updateWebhook(form.id, { ...body, isActive: form.active })
      }
      reset()
      await load()
    } catch (e) {
      setError(errText(e, 'Errore nel salvataggio'))
    } finally {
      setSaving(false)
    }
  }

  async function rotate(h: Webhook) {
    if (!(await askConfirm({ title: 'Ruota segreto', message: `Ruotare il segreto di "${h.name}"? Il vecchio smette di valere subito: aggiorna il destinatario.`, confirmLabel: 'Ruota', destructive: true }))) return
    try {
      const r = await integrationsApi.rotateSecret(h.id)
      setShown({ title: `Nuovo segreto di "${r.name}"`, value: r.secret ?? '' })
    } catch (e) {
      setError(errText(e, 'Errore nella rotazione'))
    }
  }

  async function test(h: Webhook) {
    setError(null)
    try {
      await integrationsApi.testWebhook(h.id)
      setInfo(`Evento di prova accodato per "${h.name}": l'esito compare nella scheda Consegne.`)
    } catch (e) {
      setError(errText(e, 'Errore nell\'invio della prova'))
    }
  }

  async function remove(h: Webhook) {
    if (!(await askConfirm({ title: 'Elimina webhook', message: `Eliminare il webhook "${h.name}"?`, confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await integrationsApi.removeWebhook(h.id)
      await load()
    } catch (e) {
      setError(errText(e, 'Errore nell\'eliminazione'))
    }
  }

  return (
    <>
      {error && <Callout tone="error">{error}</Callout>}
      {info && <Callout tone="info">{info}</Callout>}
      {shown && <SecretBox title={shown.title} value={shown.value} onClose={() => setShown(null)} />}

      <Card variant="outlined" padding="none">
        <CardHeader title={form.id === undefined ? 'Nuovo webhook' : 'Modifica webhook'} />
        <CardContent>
          <div className="d-flex flex-column gap-3">
            <p className="text-muted md-typescale-body-small mb-0">
              Il CRM invia un POST firmato a ogni evento scelto. Intestazioni: <code>X-Equal-Signature</code>{' '}
              (<code>t=…,v1=HMAC-SHA256(segreto, t + &quot;.&quot; + corpo)</code>, da rifiutare oltre 5 minuti di scarto),{' '}
              <code>X-Equal-Event-Id</code> (uguale a ogni ripetizione), <code>X-Equal-Event-Type</code>. Il corpo
              contiene solo identificativi e valori di vocabolario: nessun nome, telefono, email o importo.
              Sono ammessi solo indirizzi https pubblici. Una risposta non 2xx viene ripetuta con attesa crescente.
            </p>
            <FormGrid columns={3}>
              <div>
                <TextField id="wh-name" label="Nome" maxLength={150} value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="md-span-2">
                <TextField id="wh-url" label="Indirizzo (https)" maxLength={500} supportingText="es. https://esempio.it/hook/equal"
                  value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
              </div>
            </FormGrid>
            <div>
              <div className="md-typescale-title-small mb-1">Eventi</div>
              <FormGrid columns={3}>
                {types.map(t => (
                  <div key={t.code}>
                    <Checkbox id={`wh-ev-${t.code}`}
                      checked={form.codes.includes(t.code)} onChange={() => toggleCode(t.code)}
                      label={<>{t.label} <code className="md-typescale-body-small">{t.code}</code></>} />
                  </div>
                ))}
              </FormGrid>
            </div>
            {form.id !== undefined && (
              <Checkbox id="wh-active" label="Attivo" checked={form.active}
                onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
            )}
            <div className="d-flex gap-2">
              <Button variant="tonal" disabled={saving} onClick={() => void save()}>
                {form.id === undefined ? 'Crea webhook' : 'Salva'}
              </Button>
              {form.id !== undefined && (
                <Button variant="text" onClick={reset}>Annulla</Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card variant="outlined" padding="none">
        {hooks.length === 0 ? (
          <EmptyState icon="webhook" title="Nessun webhook." />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Nome</th><th>Indirizzo</th><th>Eventi</th><th>Stato</th><th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {hooks.map(h => (
                <tr key={h.id}>
                  <td className="md-typescale-emphasized-body-medium">{h.name}</td>
                  <td className="md-typescale-body-small text-break">{h.url}</td>
                  <td className="md-typescale-body-small">{h.eventCodes.join(', ')}</td>
                  <td>
                    {h.isActive
                      ? <StatusPill tone="success">Attivo</StatusPill>
                      : <StatusPill tone="neutral">Disattivato</StatusPill>}
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <IconButton icon="edit" size="sm" aria-label="Modifica"
                        onClick={() => setForm({ id: h.id, name: h.name, url: h.url, codes: h.eventCodes, active: h.isActive })} />
                      <OverflowMenu
                        size="sm"
                        aria-label="Altre azioni"
                        items={[
                          { label: 'Invia un evento di prova', icon: 'send', disabled: !h.isActive, onSelect: () => void test(h) },
                          { label: 'Ruota il segreto', icon: 'key', onSelect: () => void rotate(h) },
                        ]}
                      />
                      <IconButton icon="delete" size="sm" aria-label="Elimina"
                        onClick={() => void remove(h)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>
    </>
  )
}

function DeliveriesTab() {
  const askConfirm = useConfirm()
  const [rows, setRows] = useState<WebhookDelivery[]>([])
  const [hooks, setHooks] = useState<Webhook[]>([])
  const [subscriptionId, setSubscriptionId] = useState('')
  const [status, setStatus] = useState<WebhookDeliveryStatus | ''>('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [d, h] = await Promise.all([
        integrationsApi.deliveries({
          ...(subscriptionId ? { subscriptionId: Number(subscriptionId) } : {}),
          ...(status ? { status } : {}),
        }),
        integrationsApi.listWebhooks(),
      ])
      setRows(d)
      setHooks(h)
      setError(null)
    } catch (e) {
      setError(errText(e, 'Errore nel caricamento'))
    } finally {
      setLoading(false)
    }
  }, [subscriptionId, status])

  useEffect(() => { void load() }, [load])

  async function redeliver(d: WebhookDelivery) {
    if (!(await askConfirm({ title: 'Rimanda evento', message: 'Rimandare questo evento? Il destinatario lo riceve con lo stesso identificativo.', confirmLabel: 'Rimanda' }))) return
    try {
      await integrationsApi.redeliver(d.id)
      await load()
    } catch (e) {
      setError(errText(e, 'Errore nel rinvio'))
    }
  }

  return (
    <>
      {error && <Callout tone="error">{error}</Callout>}
      <FilterBar actions={<Button variant="outlined" size="sm" onClick={() => void load()}>Aggiorna</Button>}>
        <Select label="Webhook" value={subscriptionId}
          onChange={e => setSubscriptionId(e.target.value)}>
          <option value="">Tutti i webhook</option>
          {hooks.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
        </Select>
        <Select label="Stato" value={status}
          onChange={e => setStatus(e.target.value as WebhookDeliveryStatus | '')}>
          <option value="">Tutti gli stati</option>
          {(Object.keys(WEBHOOK_STATUS_LABELS) as WebhookDeliveryStatus[]).map(s =>
            <option key={s} value={s}>{WEBHOOK_STATUS_LABELS[s]}</option>)}
        </Select>
      </FilterBar>

      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <EmptyState icon="outbox" title="Nessuna consegna." />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Quando</th><th>Webhook</th><th>Evento</th><th>Stato</th><th>Tentativi</th>
                <th>HTTP</th><th>Durata</th><th>Errore</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map(d => (
                <tr key={d.id}>
                  <td className="md-typescale-body-small">{fmt(d.createdAt)}</td>
                  <td>{hooks.find(h => h.id === d.subscriptionId)?.name ?? `#${d.subscriptionId}`}</td>
                  <td><code className="md-typescale-body-small">{d.eventType}</code></td>
                  <td>
                    <StatusPill tone={STATUS_TONES[d.status]}>{WEBHOOK_STATUS_LABELS[d.status]}</StatusPill>
                    {d.status === 'Ritento' && d.nextAttemptAt && (
                      <div className="md-typescale-body-small text-muted">prossimo: {fmt(d.nextAttemptAt)}</div>
                    )}
                  </td>
                  <td className="md-num">{d.attempt}</td>
                  <td className="md-num">{d.httpStatus ?? '—'}</td>
                  <td className="md-num">{d.durationMs === null ? '—' : `${d.durationMs} ms`}</td>
                  <td className="md-typescale-body-small text-break">{d.errorSnippet ?? '—'}</td>
                  <td>
                    {(d.status === 'Fallita' || d.status === 'Consegnata') && (
                      <IconButton icon="replay" size="sm" aria-label="Rimanda"
                        onClick={() => void redeliver(d)} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>
    </>
  )
}

const HEALTH_TONES: Record<IntegrationHealth, PillTone> = {
  Operativa: 'success',
  Degradata: 'warning',
  NonDisponibile: 'error',
  Manutenzione: 'info',
  SenzaTraffico: 'neutral',
}

const OUTCOME_TONES: Record<string, PillTone> = {
  Successo: 'success',
  Fallito: 'error',
  Parziale: 'warning',
  RichiedeIntervento: 'error',
}

/** F-119: il registro delle chiamate di una integrazione, a pagine. Mai corpi di richiesta o risposta. */
function CallLogPanel({ item, onClose }: { item: IntegrationCenterItem; onClose: () => void }) {
  const PAGE = 25
  const [page, setPage] = useState<IntegrationCallsPage | null>(null)
  const [offset, setOffset] = useState(0)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    integrationsApi.centerCalls(item.kind, item.id, offset, PAGE)
      .then(p => { if (!cancelled) { setPage(p); setError(null) } })
      .catch((e: unknown) => { if (!cancelled) setError(errText(e, 'Errore nel caricamento')) })
    return () => { cancelled = true }
  }, [item.kind, item.id, offset])

  return (
    <Card variant="outlined" padding="none">
      <CardHeader
        title={<>Registro chiamate &middot; {item.name}</>}
        actions={<Button variant="text" size="sm" onClick={onClose}>Chiudi</Button>}
      />
      <div className="d-flex flex-column gap-3">
        {error && <div className="px-3"><Callout tone="error">{error}</Callout></div>}
        {page && !page.available && (
          <div className="px-3"><Callout tone="warning">Il registro delle chiamate non è ancora disponibile (migration da applicare).</Callout></div>
        )}
        {page?.available && page.items.length === 0 && (
          <div className="px-3 pb-3 text-muted">Nessuna chiamata registrata.</div>
        )}
        {page?.available && page.items.length > 0 && (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Quando</th><th>Richiesta</th><th>Esito</th><th>HTTP</th><th>Durata</th><th>Tentativo</th><th>Codice</th><th>Dettaglio</th>
              </tr>
            </thead>
            <tbody>
              {page.items.map(c => (
                <tr key={c.id}>
                  <td className="text-nowrap">{fmt(c.occurredAt)}</td>
                  <td className="font-monospace md-typescale-body-small">{c.operation ?? '—'}</td>
                  <td><StatusPill tone={OUTCOME_TONES[c.outcome] ?? 'neutral'}>{c.outcome}</StatusPill></td>
                  <td className="md-num">{c.httpStatus ?? '—'}</td>
                  <td className="md-num">{c.durationMs === null ? '—' : `${c.durationMs} ms`}</td>
                  <td className="md-num">{c.attempt ?? '—'}</td>
                  <td className="font-monospace md-typescale-body-small">{c.errorCode ?? '—'}</td>
                  <td className="md-typescale-body-small text-break">
                    {c.errorSnippet ?? '—'}
                    {c.entityType && c.entityId !== null ? ` · ${c.entityType} #${c.entityId}` : ''}
                    {c.contractId !== null ? ` · pratica #${c.contractId}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </div>
      {page?.available && page.total > PAGE && (
        <div className="d-flex justify-content-between align-items-center p-3">
          <span className="md-typescale-body-small text-muted">{offset + 1}&ndash;{Math.min(offset + PAGE, page.total)} di {page.total}</span>
          <div className="d-flex gap-2">
            <Button variant="outlined" size="sm" disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE))}>Più recenti</Button>
            <Button variant="outlined" size="sm" disabled={offset + PAGE >= page.total}
              onClick={() => setOffset(offset + PAGE)}>Meno recenti</Button>
          </div>
        </div>
      )}
    </Card>
  )
}

/** F-119 (REQ-225-001): panoramica delle integrazioni con stato di salute, contatori e interruttore di manutenzione. */
function OverviewTab() {
  const [data, setData] = useState<IntegrationCenter | null>(null)
  const prompt = usePrompt()
  const [error, setError] = useState<string | null>(null)
  const [log, setLog] = useState<IntegrationCenterItem | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await integrationsApi.center())
      setError(null)
    } catch (e) {
      setError(errText(e, 'Errore nel caricamento'))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function toggleMaintenance(item: IntegrationCenterItem) {
    const turningOn = !item.maintenance.on
    let note: string | null = null
    if (turningOn) {
      note = await prompt({
        title: 'Attiva la manutenzione',
        message: 'Nota sulla manutenzione (facoltativa, al massimo 200 caratteri).',
        label: 'Nota',
        initialValue: '',
        confirmLabel: 'Attiva',
      })
      if (note === null) return
    }
    const key = `${item.kind}:${item.id}`
    setBusy(key)
    try {
      await integrationsApi.setMaintenance(item.kind, item.id, { on: turningOn, note: note || null })
      await load()
    } catch (e) {
      setError(errText(e, 'Errore nel salvataggio'))
    } finally {
      setBusy(null)
    }
  }

  if (error) return <Callout tone="error">{error}</Callout>
  if (!data) return <LoadingState />

  return (
    <>
      <SyncPauseCard />
      <SkyCanvassCard />
      {!data.available && (
        <Callout tone="warning">
          Il registro delle chiamate non è ancora disponibile: le tabelle non esistono finché non si applica la migration.
        </Callout>
      )}
      {data.available && !data.callLogEnabled && (
        <Callout tone="info">
          Il registro delle chiamate è spento: nessuna chiamata viene registrata e ogni integrazione risulta &laquo;Senza traffico&raquo;.
          Si accende al deploy con la variabile d'ambiente <code>Integrations__CallLog__Enabled=true</code> oppure con il flag <code>integrazioni.ritentativi</code>.
        </Callout>
      )}
      {data.available && data.items.length === 0 && <div className="text-muted">Nessuna integrazione configurata.</div>}
      {data.items.length > 0 && (
        <Card variant="outlined" padding="none">
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Integrazione</th><th>Stato</th><th>Ultima sincronizzazione</th><th>Ultimo errore</th>
                <th className="text-end">Chiamate 24 h</th><th className="text-end">% successo</th>
                <th className="text-end">Durata media</th><th className="text-end">Coda</th>
                <th className="text-end">Ritentativi 24 h</th><th />
              </tr>
            </thead>
            <tbody>
              {data.items.map(i => (
                <tr key={`${i.kind}:${i.id}`} className={i.active ? '' : 'text-muted'}>
                  <td>
                    <div className="md-typescale-emphasized-body-medium">{i.name}</div>
                    <div className="md-typescale-body-small text-muted">
                      {i.type} &middot; {i.direction === 'out' ? 'in uscita' : 'in entrata'}{i.active ? '' : ' · disattivata'}
                    </div>
                  </td>
                  <td>
                    <StatusPill tone={HEALTH_TONES[i.health]}>{INTEGRATION_HEALTH_LABELS[i.health]}</StatusPill>
                    {i.maintenance.on && i.maintenance.note && <div className="md-typescale-body-small text-muted">{i.maintenance.note}</div>}
                    {i.maintenance.on && i.maintenance.until && (
                      <div className="md-typescale-body-small text-muted">fino al {fmt(i.maintenance.until)}</div>
                    )}
                  </td>
                  <td className="md-typescale-body-small">{fmt(i.lastSuccessAt)}</td>
                  <td className="md-typescale-body-small text-break">
                    {i.lastError
                      ? <>{fmt(i.lastError.at)}<br /><span className="font-monospace">{i.lastError.code ?? '—'}</span>
                        {i.lastError.snippet ? ` · ${i.lastError.snippet}` : ''}</>
                      : '—'}
                  </td>
                  <td className="text-end md-num">{i.calls24h}</td>
                  <td className="text-end md-num">{i.successRatePct === null ? '—' : `${i.successRatePct}%`}</td>
                  <td className="text-end md-num">{i.avgDurationMs === null ? '—' : `${i.avgDurationMs} ms`}</td>
                  <td className="text-end md-num">{i.queue === null ? 'n/d' : i.queue}</td>
                  <td className="text-end md-num">{i.retries24h}</td>
                  <td className="text-nowrap text-end">
                    <div className="d-flex gap-1 justify-content-end">
                      <Button variant="outlined" size="sm" onClick={() => setLog(i)}>
                        Registro
                      </Button>
                      <Button variant={i.maintenance.on ? 'tonal' : 'outlined'} size="sm"
                        disabled={busy === `${i.kind}:${i.id}`} onClick={() => void toggleMaintenance(i)}>
                        {i.maintenance.on ? 'Termina manutenzione' : 'Manutenzione'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </Card>
      )}
      {data.thresholds && (
        <div className="md-typescale-body-small text-muted">
          Stato calcolato sugli ultimi {data.windowMinutes} minuti: degradata dal {data.thresholds.degradedFailureRatePct}% di errori
          (almeno {data.thresholds.minSample} chiamate), non disponibile dopo {data.thresholds.unavailableConsecutiveFailures} fallimenti
          di fila. Avvisi agli Admin {data.healthSweeperEnabled ? 'attivi' : 'spenti'}.
        </div>
      )}
      {log && <CallLogPanel item={log} onClose={() => setLog(null)} />}
    </>
  )
}

export function IntegrationsPage() {
  const [tab, setTab] = useState<Tab>('overview')
  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Panoramica' },
    { id: 'keys', label: 'Chiavi lead' },
    { id: 'ads', label: 'Lead Ads' },
    { id: 'webhooks', label: 'Webhook' },
    { id: 'deliveries', label: 'Consegne' },
  ]

  return (
    <Page>
      <PageHeader
        title="Integrazioni"
        icon="hub"
        back={{ to: '/settings', label: 'Impostazioni generali' }}
        tabs={
          <Tabs
            aria-label="Sezioni integrazioni"
            items={tabs}
            value={tab}
            onChange={id => setTab(id as Tab)}
          />
        }
      />
      {tab === 'overview' && <OverviewTab />}
      {tab === 'keys' && <KeysTab />}
      {tab === 'ads' && <LeadAdsTab />}
      {tab === 'webhooks' && <WebhooksTab />}
      {tab === 'deliveries' && <DeliveriesTab />}
    </Page>
  )
}
